import {issueHovercardPath} from '@github-ui/paths'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import {useIssueState} from '@github-ui/use-issue-state'
import {Link, Token} from '@primer/react'
import type React from 'react'
import {graphql, useFragment} from 'react-relay'

import {useCachePreheatIssueViewer} from '../../hooks/use-cache-preheat-issueviewer'
import {getIssueTitle} from '../../utils/helpers'
import {getParentIssueUrl} from '../../utils/urls'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderParentTitle$data, HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'
import dividerLineStyles from './HeaderMetadataDivider.module.css'
import styles from './HeaderParentTitle.module.css'

type HeaderParentTitleProps = {
  parentKey?: HeaderParentTitle$key
  small?: boolean
} & SharedProps

type HeaderParentTitleInternalProps = {
  parent: NonNullable<HeaderParentTitle$data['parent']>
  small?: boolean
} & SharedProps

type SharedProps = {
  optionConfig?: OptionConfig
  repositoryNameWithOwner: string
}

/** The internal component that renders the parent title itself after all data fetching has occurred */
export const HeaderParentTitle = ({
  parentKey,
  optionConfig,
  small = false,
  repositoryNameWithOwner,
}: HeaderParentTitleProps) => {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment HeaderParentTitle on Issue {
        parent {
          id
          number
          repository {
            name
            owner {
              login
            }
          }
          state
          stateReason
          title
          titleHTML
          url
        }
      }
    `,
    parentKey,
  )

  if (!data) {
    return null
  }
  const {parent} = data

  // The data from this fragment is not _all_ available yet, as it's requested in the secondary query, however,
  // AddSubIssueButtonGroup requests parent.id which is available in the primary query. useFragment, seemingly
  // merges this partial data and we are left with a parent object that is not empty, but also not fully populated.
  // See: https://github.com/github/github/issues/333236 and https://github.com/github/issues/issues/17680
  if (!parent?.repository || !parent.repository.owner) {
    return null
  }

  return (
    <HeaderParentTitleInternal
      parent={parent}
      optionConfig={optionConfig}
      small={small}
      repositoryNameWithOwner={repositoryNameWithOwner}
    />
  )
}

const HeaderParentTitleInternal = ({
  parent,
  optionConfig,
  small = false,
  repositoryNameWithOwner,
}: HeaderParentTitleInternalProps) => {
  const repo = parent.repository.name
  const owner = parent.repository.owner.login

  useCachePreheatIssueViewer({
    owner,
    repo,
    number: parent.number,
    source: 'HeaderParentTitle',
    featureFlag: 'issues_preheating_parent_issue',
  })

  const {sourceIcon} = useIssueState({
    state: parent.state,
    stateReason: parent.stateReason,
  })

  const IconComponent = sourceIcon('Issue')

  if (!IconComponent) return null

  const handleOpen = (e: React.MouseEvent | React.KeyboardEvent) => {
    // If in the side panel and the parent issue is the same as the current issue, just close the side panel
    if (optionConfig?.onParentIssueActivate) {
      const itemIdentifier = {number: parent.number, repo, owner, type: 'Issue'} as const
      const closedSidePanel = optionConfig.onParentIssueActivate(e, itemIdentifier)
      if (closedSidePanel) return
    }

    // Else if in the side panel and the parent issue is different than the current issue,
    // open the parent issue in the side panel
    if (optionConfig?.insideSidePanel && optionConfig?.onSubIssueClick) {
      const {id, number, state, title, url} = parent
      const subIssueItem: SubIssueSidePanelItem = {
        id: parseInt(id, 10),
        number,
        owner,
        repo,
        state,
        title,
        url,
      }
      e.preventDefault()
      optionConfig.onSubIssueClick(subIssueItem)
    }
    if (optionConfig?.navigate) {
      // If not in the side panel, navigate to the parent issue
      e.preventDefault()
      const parentRepoNameWithOwner = `${owner}/${repo}`
      optionConfig?.navigate?.(
        getParentIssueUrl(repositoryNameWithOwner, parentRepoNameWithOwner, parent.number, parent.url),
      )
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
    handleOpen(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key !== 'Enter' || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
    handleOpen(e)
  }

  const title = getIssueTitle(parent.title, parent.titleHTML) as SafeHTMLString

  const tokenContent = (
    <div className={styles.parentTitleContainer}>
      {/* eslint-disable-next-line react-hooks/static-components */}
      <IconComponent size={14} />
      <span className={styles.parentLabel}>Parent:</span>
      <Link
        aria-label={`Parent issue: ${parent.title}`}
        inline
        muted
        hoverColor="fg.muted"
        href={parent.url}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-hovercard-url={issueHovercardPath({
          owner,
          repo,
          issueNumber: parent.number,
        })}
        className={styles.parentTitleLink}
      >
        {small ? `#${parent.number}` : <VerifiedHTMLText html={title} className="markdown-title" />}
      </Link>
    </div>
  )

  if (small) return tokenContent

  return (
    <>
      <span className={dividerLineStyles.dividerLine} />
      <div className={styles.parentTitleWrapper}>
        <Token text={tokenContent} size={'xlarge'} className={styles.parentTitleToken} />
      </div>
    </>
  )
}

try{ HeaderParentTitle.displayName ||= 'HeaderParentTitle' } catch {}
try{ HeaderParentTitleInternal.displayName ||= 'HeaderParentTitleInternal' } catch {}
try{ IconComponent.displayName ||= 'IconComponent' } catch {}